import axios, { AxiosInstance, AxiosError } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Guard to prevent multiple simultaneous 401 redirects
let isRedirectingToLogin = false;

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const language = localStorage.getItem('i18nextLng') || 'pt';
    config.headers['Accept-Language'] = language;

    // Send current project ID with all requests
    const currentProjectId = localStorage.getItem('currentProjectId');
    if (currentProjectId) {
      config.headers['X-Project-ID'] = currentProjectId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Don't redirect if already on login/auth pages (prevents loop)
      const isOnAuthPage = ['/login', '/register', '/forgot-password', '/sso'].includes(window.location.pathname);

      // Don't redirect for auth-related requests (login, register, etc.)
      const isAuthRequest = error.config?.url?.includes('/auth/login') ||
                            error.config?.url?.includes('/auth/register') ||
                            error.config?.url?.includes('/auth/sso-exchange');

      if (!isOnAuthPage && !isAuthRequest && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('currentProjectId');
        window.location.href = '/login';
      }
    }

    // Handle 403 - clear stale project ID if the project is no longer accessible
    if (error.response?.status === 403) {
      const data = error.response?.data as any;
      const errorCode = data?.message;
      if (errorCode?.includes('Acesso negado ao projeto') || errorCode?.includes('access_denied')) {
        localStorage.removeItem('currentProjectId');
      }

      // Limite de plano OU assinatura ausente/vencida: toast global único com CTA de upgrade.
      // Marca o erro como já-tratado para os catches locais não duplicarem o toast.
      const handledCodes = ['PLAN_LIMIT_EXCEEDED', 'SUBSCRIPTION_REQUIRED', 'PAYMENT_PAST_DUE'];
      if (handledCodes.includes(data?.code)) {
        (error as any).__handledByInterceptor = true;
        const { resource, current, limit } = data?.data || {};
        // import dinâmico evita ciclo api.ts -> hooks -> services
        import('../utils/planLimitToast').then(({ showPlanLimitToast }) => {
          showPlanLimitToast(resource, current, limit, data?.code);
        }).catch(() => { /* noop */ });
      }
    }

    // Sem créditos de IA (a partir do início da cobrança): o backend devolve 402 ou repassa a
    // mensagem no erro da funcionalidade. Um aviso só, mesmo com várias chamadas falhando juntas.
    const corpo = error.response?.data as any;
    const textoErro = String(corpo?.message || corpo?.error || '');
    if (corpo?.code === 'AI_NO_CREDITS' || textoErro.includes('Sem créditos de IA')) {
      (error as any).__handledByInterceptor = true;
      import('react-hot-toast').then(({ default: toast }) => {
        toast.error(textoErro || 'Sem créditos de IA. Recarregue para continuar.', { id: 'ai-no-credits', duration: 8000 });
      }).catch(() => { /* noop */ });
      // Abre a recarga por cima da tela (AiCreditsProvider): o formulário em andamento fica como está
      try { window.dispatchEvent(new CustomEvent('devskin:ai-no-credits')); } catch { /* noop */ }
    }

    return Promise.reject(error);
  }
);

export default api;
